import React, { useEffect, useState } from 'react';

import { Box, BoxProps, Center, Image, Loader, UnstyledButton, useMantineTheme } from '@mantine/core';
import { FileWithPath } from '@mantine/dropzone';
import { IconX } from '@tabler/icons-react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { ChatAttachment, ChatAttachmentType } from 'shared/interfaces/ChatMessage';

interface SmartAssistantChatImageAttachmentPreviewProps extends BoxProps {
  imageFile: FileWithPath;
  smartAssistantId: string;
  conversationId: string;
  onRemove: (imageFile: FileWithPath, image?: ChatAttachment) => void;
  onUpload: (image: ChatAttachment) => void;
  setErrorMessage: (errorMessage: string | null) => void;
  isGuest?: boolean;
}

export function SmartAssistantChatImageAttachmentPreview({
  imageFile,
  smartAssistantId,
  conversationId,
  onRemove,
  onUpload,
  setErrorMessage,
  isGuest = false,
  ...boxProps
}: SmartAssistantChatImageAttachmentPreviewProps) {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  const [imageChatAttachment, setImageChatAttachment] = useState<ChatAttachment>();

  const uploadImageAttachment = useMutation({
    mutationFn: (isGuest ? GuestSmartAssistantService : SmartAssistantService).uploadAttachment,
    onSuccess(chatAttachment: ChatAttachment) {
      onUpload(chatAttachment);
      setImageChatAttachment(chatAttachment);
    },
    onError: () => {
      setErrorMessage(t('FEED_BACK.SMART_ASSISTANT_INPUT.FAILURE.IMAGE_UPLOAD'));
      onRemove(imageFile);
    }
  });

  useEffect(() => {
    uploadImageAttachment.mutate({
      smartAssistantId,
      conversationId,
      type: ChatAttachmentType.IMAGE,
      file: imageFile
    });
  }, [imageFile]);

  return (
    <Box pos="relative" {...boxProps}>
      <Image
        src={!uploadImageAttachment.isLoading ? imageChatAttachment?.displayUrl : undefined}
        width={60}
        height={60}
        radius="md"
        fit="cover"
        withPlaceholder
        placeholder={
          <Center>
            <Loader size="xs" />
          </Center>
        }
      />
      {!uploadImageAttachment.isLoading && (
        <UnstyledButton
          onClick={() => imageChatAttachment && onRemove(imageFile, imageChatAttachment)}
          style={{
            width: 16,
            height: 16,
            backgroundColor: theme.colors.gray[1],
            borderRadius: '50%',
            position: 'absolute',
            border: `1px solid ${theme.colors.gray[5]}`,
            top: 2,
            right: 2,
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
          <IconX
            color={theme.colors.gray[5]}
            style={{
              width: 8,
              height: 8,
              strokeWidth: 3
            }}
          />
        </UnstyledButton>
      )}
    </Box>
  );
}
